import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { patchCompany } from '../../../api/company/company';
import { useNotification } from '../../../context/NotificationContext';
import { useCompany } from '../../../CompanyContext';
import { useTheme } from '@mui/material/styles';
import AvatarUpload from '../AvatarUpload';
import GradientButton from '../../buttons/GradientButton';
import { useSettingsCompletion } from '../../tutorial';
import { useSaveSuccess } from '../../tutorial/hooks/useSaveSuccess';
import { SaveSuccess } from '../../tutorial/feedback/SaveSuccess';
import { useLocation } from 'react-router-dom';
import { useTutorial } from '../../tutorial/TutorialContext';
import TutorialBanner from '../../tutorial/ui/TutorialBanner';
import './BusinessInformation/BusinessInformation.css';

// US States Array
const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Timezones Array
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Arizona (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' }
];

const BusinessInformation = () => {
  const location = useLocation();
  const { markStepComplete } = useTutorial();
  const fromTutorial = Boolean(location.state?.fromTutorial);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { user, companyData, updateCompanyData } = useAuth();
  const { companyName, updateCompanyName } = useCompany();
  const [loading, setLoading] = useState(false);
  const { setNotification } = useNotification();

  // Pass the step ID (1) for Company Profile from the tutorial checklist
  const [showSuccess, triggerSuccess] = useSaveSuccess(1);

  const cssVars = useMemo(() => {
    const surface = isDark ? '#0f172a' : '#ffffff';
    const surface2 = isDark ? 'rgba(15, 23, 42, 0.72)' : 'rgba(255, 255, 255, 0.7)';
    const text = isDark ? '#e5e7eb' : '#0f172a';
    const muted = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? 'rgba(148, 163, 184, 0.22)' : 'rgba(15, 23, 42, 0.12)';
    const shadow = isDark ? '0 10px 30px rgba(0,0,0,0.35)' : '0 10px 30px rgba(2, 6, 23, 0.08)';
    const inputBg = isDark ? 'rgba(2, 6, 23, 0.35)' : 'rgba(255, 255, 255, 0.9)';
    const inputBgFocus = isDark ? 'rgba(2, 6, 23, 0.55)' : '#ffffff';
    const shimmerA = isDark ? 'rgba(226, 232, 240, 0.08)' : 'rgba(15, 23, 42, 0.06)';
    const shimmerB = isDark ? 'rgba(226, 232, 240, 0.14)' : 'rgba(15, 23, 42, 0.12)';

    return {
      '--bi-surface': surface,
      '--bi-surface-2': surface2,
      '--bi-text': text,
      '--bi-muted': muted,
      '--bi-border': border,
      '--bi-shadow': shadow,
      '--bi-focus': 'rgba(72, 138, 236, 0.45)',
      '--bi-input-bg': inputBg,
      '--bi-input-bg-focus': inputBgFocus,
      '--bi-shimmer-a': shimmerA,
      '--bi-shimmer-b': shimmerB
    };
  }, [isDark]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    website: '',
    timezone: 'America/New_York',
    mobileBusiness: false,
    address: {
      street1: '',
      street2: '',
      city: '',
      state: '',
      zipCode: '',
    }
  });

  // Initialize settings completion hook
  const { handleSave: handleSaveWithCompletion } = useSettingsCompletion(formData, async () => {
    try {
      setLoading(true);
      if (!user || !companyData) throw new Error('No authenticated user or company data');

      // Clean phone number before saving (remove formatting)
      const cleanPhone = formData.phone.replace(/\D/g, '');
      
      const updatedCompanyData = {
        name: formData.name,
        email: formData.email,
        phone: cleanPhone.length === 10 ? formData.phone : formData.phone,
        businessInfo: {
          ...companyData.businessInfo,
          email: formData.email,
          website: formData.website,
          timezone: formData.timezone,
          mobileBusiness: Boolean(formData.mobileBusiness),
          address: {
            street1: formData.address.street1,
            street2: formData.address.street2,
            city: formData.address.city,
            state: formData.address.state,
            stateLabel: US_STATES.find(state => state.value === formData.address.state)?.label || '',
            zipCode: formData.address.zipCode,
            fullAddress: `${formData.address.street1}${formData.address.street2 ? `, ${formData.address.street2}` : ''}, ${formData.address.city}, ${formData.address.state} ${formData.address.zipCode}`
          },
          searchKeywords: [
            formData.name.toLowerCase(),
            formData.email.toLowerCase(),
            formData.address.city.toLowerCase(),
            formData.address.state.toLowerCase(),
            US_STATES.find(state => state.value === formData.address.state)?.label.toLowerCase() || '',
            formData.address.zipCode,
          ].filter(Boolean),
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.uid
      };

      const company = await patchCompany({
        companyId: companyData.id,
        company: updatedCompanyData,
      });

      updateCompanyData({
        ...companyData,
        ...company,
        ...updatedCompanyData
      });
      
      // Update company name in context
      updateCompanyName(formData.name);

      // Only show the tutorial success message
      triggerSuccess();

      return true; // Indicate successful save
    } catch (error) {
      console.error('Error updating business information:', error);
      setNotification({
        open: true,
        message: `Error updating business information: ${error.message}`,
        severity: 'error'
      });
      return false; // Indicate failed save
    } finally {
      setLoading(false);
    }
  });

  // Load saved data when companyData changes
  useEffect(() => {
    if (companyData) {
      setFormData({
        name: companyData.name || '',
        email: companyData.businessInfo?.email || companyData.email || '',
        phone: companyData.phone || '',
        website: companyData.businessInfo?.website || '',
        timezone: companyData.businessInfo?.timezone || 'America/New_York',
        mobileBusiness: Boolean(companyData.businessInfo?.mobileBusiness) || false,
        address: {
          street1: companyData.businessInfo?.address?.street1 || '',
          street2: companyData.businessInfo?.address?.street2 || '',
          city: companyData.businessInfo?.address?.city || '',
          state: companyData.businessInfo?.address?.state || '',
          zipCode: companyData.businessInfo?.address?.zipCode || '',
        }
      });
    }
  }, [companyData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    
    if (name === 'phone') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');
      
      // Format the phone number
      let formattedPhone = '';
      if (numericValue.length <= 3) {
        formattedPhone = numericValue;
      } else if (numericValue.length <= 6) {
        formattedPhone = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3)}`;
      } else {
        formattedPhone = `(${numericValue.slice(0, 3)}) ${numericValue.slice(3, 6)}-${numericValue.slice(6, 10)}`;
      }

      setFormData(prev => ({
        ...prev,
        phone: formattedPhone
      }));
      return;
    }

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Update the save handler to use the wrapped version
  const handleSave = (e) => {
    if (e) e.preventDefault();
    handleSaveWithCompletion();
  };

  // Auto-save Mobile Business toggle
  const handleMobileBusinessToggle = async (event, checked) => {
    const newValue = Boolean(checked);
    // Optimistic UI update
    setFormData(prev => ({ ...prev, mobileBusiness: newValue }));
    try {
      if (!user || !companyData?.id) throw new Error('Missing user/company');
      const company = await patchCompany({
        companyId: companyData.id,
        company: {
          businessInfo: {
            ...companyData.businessInfo,
            mobileBusiness: newValue,
          },
          updatedBy: user.uid,
        },
      });

      updateCompanyData({
        ...companyData,
        ...company,
        businessInfo: {
          ...companyData.businessInfo,
          mobileBusiness: newValue
        }
      });
    } catch (error) {
      console.error('Error updating mobile business setting:', error);
      // Revert on error
      setFormData(prev => ({ ...prev, mobileBusiness: !newValue }));
      setNotification({
        open: true,
        message: `Failed to update setting: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleMobileBusinessInputChange = (e) => {
    handleMobileBusinessToggle(e, e.target.checked);
  };

  return (
    <div className="businessInfoPage" style={cssVars}>
      <div className="businessInfoInner">
        {fromTutorial && (
          <TutorialBanner
            description="Add your company name, contact details, website, timezone and address. This information appears on client communications."
            onDismiss={() => { /* no-op; banner hides when navigating away */ }}
          />
        )}

        <div className="businessInfoHeader">
          <div>
            <h1>Business Information</h1>
            <p>Keep your company details accurate for client communications.</p>
          </div>
        </div>

        <div className="businessInfoGrid">
          <section className="biCard" aria-label="Company profile">
            <div className="biCardHeader">
              <h2>Company Profile</h2>
              <p className="sub">Logo and primary contact details.</p>
            </div>
            <div className="biCardBody">
              <AvatarUpload
                company={companyData}
                setNotification={setNotification}
                updateCompany={updateCompanyData}
              />

              <div className="biFormGrid">
                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-name">Business name</label>
                  </div>
                  <input
                    id="bi-name"
                    className="biInput"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="organization"
                  />
                </div>

                <div className="biField">
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-email">Business email</label>
                  </div>
                  <input
                    id="bi-email"
                    className="biInput"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>

                <div className="biField">
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-phone">Business phone</label>
                  </div>
                  <input
                    id="bi-phone"
                    className="biInput"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    disabled={loading}
                    inputMode="tel"
                    maxLength={14}
                    placeholder="(555) 555-5555"
                    autoComplete="tel"
                  />
                  <p className="biHelperText">Format: (555) 555-5555</p>
                </div>

                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-website">Website</label>
                    <span className="biHint">Optional</span>
                  </div>
                  <input
                    id="bi-website"
                    className="biInput"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="https://yourcompany.com"
                    autoComplete="url"
                  />
                </div>

                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-timezone">Timezone</label>
                  </div>
                  <select
                    id="bi-timezone"
                    className="biInput"
                    name="timezone"
                    value={formData.timezone}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {TIMEZONES.map(tz => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>

          <div className="businessInfoCol2">
            <section className="biCard" aria-label="Address">
              <div className="biCardHeader">
                <h2>Address</h2>
                <p className="sub">Used for directions, service areas, and customer messaging.</p>
              </div>
              <div className="biCardBody">
                <div className="biFormGrid">
                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-street1">Street address</label>
                  </div>
                  <input
                    id="bi-street1"
                    className="biInput"
                    name="address.street1"
                    value={formData.address.street1}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="address-line1"
                  />
                </div>

                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-street2">Suite / Unit #</label>
                    <span className="biHint">Optional</span>
                  </div>
                  <input
                    id="bi-street2"
                    className="biInput"
                    name="address.street2"
                    value={formData.address.street2}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="address-line2"
                  />
                </div>

                <div className="biField">
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-city">City</label>
                  </div>
                  <input
                    id="bi-city"
                    className="biInput"
                    name="address.city"
                    value={formData.address.city}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="address-level2"
                  />
                </div>

                <div className="biField">
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-state">State</label>
                  </div>
                  <select
                    id="bi-state"
                    className="biInput"
                    name="address.state"
                    value={formData.address.state}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="address-level1"
                  >
                    <option value="">Select…</option>
                    {US_STATES.map(s => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="biField" style={{ gridColumn: '1 / -1' }}>
                  <div className="biFieldLabelRow">
                    <label className="biLabel" htmlFor="bi-zip">ZIP code</label>
                  </div>
                  <input
                    id="bi-zip"
                    className="biInput"
                    name="address.zipCode"
                    value={formData.address.zipCode}
                    onChange={handleChange}
                    disabled={loading}
                    inputMode="numeric"
                    autoComplete="postal-code"
                  />
                </div>
              </div>

              <div className="biActions">
                <span className="biSavingNote">{loading ? 'Saving changes…' : ''}</span>
                <GradientButton
                  variant="primary"
                  onClick={handleSave}
                  disabled={loading}
                  size="small"
                  sx={{
                    minWidth: 220,
                    opacity: loading ? 0.7 : 1
                  }}
                >
                  {loading ? 'Saving…' : 'Save Changes'}
                </GradientButton>
              </div>
            </div>
          </section>

            <section className="biCard" aria-label="Mobile business">
              <div className="biCardHeader">
                <h2>Mobile business</h2>
                <p className="sub">How your team meets clients relative to your business address.</p>
              </div>
              <div className="biCardBody">
                <label
                  className={`bi-check-row${loading ? ' bi-check-row--disabled' : ''}`}
                  htmlFor="bi-mobile-business"
                >
                  <input
                    id="bi-mobile-business"
                    type="checkbox"
                    className="bi-checkbox"
                    checked={formData.mobileBusiness}
                    onChange={handleMobileBusinessInputChange}
                    disabled={loading}
                  />
                  <span className="bi-check-row__body">
                    <span className="bi-check-row__title">Mobile business</span>
                    <span className="bi-check-row__desc">
                      Turn on if your team travels to clients. Enables Get Directions to client addresses.
                    </span>
                  </span>
                </label>
              </div>
            </section>
          </div>
        </div>
      </div>

      <SaveSuccess show={showSuccess} message="Business information saved successfully!" />
    </div>
  );
};

export default BusinessInformation;